import {ElementRef, Inject, Injectable} from "@angular/core";
import {ReplaySubject} from "rxjs";
import {Cart} from "../domain/cart";
import {CacheService} from "../cache/cache.service";
import {CartItem} from "../domain/cartitem";
import {
  ExchangePriceWithRelations,
  ProductControllerService,
  ProductWithRelations
} from "@aparraga/ahoracrypto-api-sdk/sdk";
import {EventsTrackerService} from "../eventstracker/eventstracker.service";
import {IS_BROWSER} from "../utils/injectiontokens";
import {LocalStorageService} from "../storage/local-storage.service";


@Injectable({
  providedIn: 'root'
})
export class CartService {

  protected shoppingCart: Cart;
  public shoppingCart$ = new ReplaySubject<Cart>();

  public activeCartLink: ElementRef;

  protected timeoutToHidePopover: any;

  constructor(@Inject(IS_BROWSER) protected isBrowser: boolean,
              protected cacheService: CacheService,
              protected eventsTrackerService: EventsTrackerService,
              protected productControllerService: ProductControllerService,
              protected storageService: LocalStorageService) {

    if(this.isBrowser) {
      this.shoppingCart = this.storageService.getObject<Cart>("Cart");
      if(this.shoppingCart != null) {
        this.shoppingCart$.next(this.shoppingCart);
      }
    }

    if(this.shoppingCart == null) {
      this.shoppingCart = new Cart();
      this.shoppingCart.cartItems = [];
    }

  }

  public setActiveCartLink(cartLink: ElementRef) {
    this.activeCartLink = cartLink;
  }

  public getItemsCount() {
    let returnValue = 0;
    this.shoppingCart.cartItems.forEach((cartItem: CartItem) => {
      returnValue += cartItem.quantity;
    });
    return returnValue;
  }

  public clearAndAddItem(product: ProductWithRelations, exchangePrice: ExchangePriceWithRelations, quantity: number, totalToPayFor: number, cryptoQuantityCanVary: boolean, walletAddress: string = null, walletNetwork: string = null, walletMemo: string = null) {
    this.shoppingCart.cartItems = [];
    this.addItem(product, exchangePrice, quantity, totalToPayFor, cryptoQuantityCanVary, walletAddress, walletNetwork, walletMemo);
  }

  public addItem(product: ProductWithRelations, exchangePrice: ExchangePriceWithRelations, quantity: number, totalToPayFor: number, cryptoQuantityCanVary: boolean, walletAddress: string = null, walletNetwork: string = null, walletMemo: string = null) {

    //ensure that product has stock before adding it to the cart
    let found = false;

    //if the item already exists in the shopping cart, just sum the quantity
    this.shoppingCart.cartItems.forEach((existingItem: CartItem) => {
      if (existingItem.product.id == product.id) {
        found = true;
      }
    });

    //otherwise create a new cart item line
    if (!found) {
      let cartItem = new CartItem();
      cartItem.product = product;
      cartItem.exchangePrice = exchangePrice;
      cartItem.quantity = quantity;
      cartItem.cryptoQuantityCanVary = cryptoQuantityCanVary;
      cartItem.totalToPayFor = totalToPayFor;
      cartItem.walletAddress = walletAddress;
      cartItem.walletNetwork = walletNetwork;
      cartItem.walletMemo = walletMemo;

      this.shoppingCart.cartItems.push(cartItem);
      this.eventsTrackerService.trackAddToCart(product, quantity);
      this.shoppingCart$.next(this.shoppingCart);
      this.saveCart();
    }
    else {
      this.changeItemQuantity(product, quantity);
    }

  }

  public clearCart() {
    this.shoppingCart = new Cart();
    this.shoppingCart.cartItems = [];
    this.shoppingCart$.next(this.shoppingCart);
    this.saveCart();
  }

  public changeItemQuantity(product: ProductWithRelations, quantity: number) {

    if(quantity > 0) {
      this.shoppingCart.cartItems.forEach((cartItem: CartItem) => {
        if (cartItem.product.id == product.id && cartItem.quantity != quantity) {
          if(cartItem.quantity > quantity) {
            this.eventsTrackerService.trackRemoveFromCart(product, quantity);
          }
          else {
            this.eventsTrackerService.trackAddToCart(product, quantity);
          }
          cartItem.quantity = quantity;
        }
      });
      this.shoppingCart$.next(this.shoppingCart);
      this.saveCart();
    }
    else {
      this.removeItem(product);
    }
  }

  public getItems(): CartItem[] {
    return this.shoppingCart.cartItems;
  }

  public getCart(): Cart {
    return this.shoppingCart;
  }

  public removeItem(product: ProductWithRelations) {

    this.shoppingCart.cartItems.forEach((cartItem: CartItem) => {
      if (cartItem.product.id == product.id) {
        this.eventsTrackerService.trackRemoveFromCart(product, 0);
      }
    });
    this.shoppingCart.cartItems = this.shoppingCart.cartItems.filter((cartItem: CartItem) => cartItem.product.id != product.id);
    this.shoppingCart$.next(this.shoppingCart);
    this.saveCart();
  }

  public saveCart() {
    if(this.isBrowser) {
      this.storageService.setObject('Cart', this.shoppingCart);
    }
  }


}
